import {LocationMap} from "@/shared/locationMap";
import {GeocodeService} from "@/shared/googleMapsAutocomplete";
import {htmlEscape} from "@/shared/utils/string";
import {Country, LocationRestrictionRadiusOption} from "@/shared/timr";
import {loadGoogleMaps} from "@/shared/task/googleMapsLoader";

interface LocationPanelAddressData {
    latitude?: number;
    longitude?: number;
    address?: string;
    city?: string;
    zipCode?: string;
    state?: string;
    countryName?: string;
    countryId?: number;
    countryCode?: string;
}

export interface LocationPanelData extends LocationPanelAddressData {
    locationRestrictionRadius?: number;
    locationRestrictionInherited?: boolean;
}

export class LocationPanel extends HTMLElement {
    public static readonly EVENT_POSITION_CHANGED = "positionChanged";
    public static readonly EVENT_ADDRESS_CHANGED = "addressChanged";
    private static readonly INHERITED_RADIUS_OPTION_VALUE = "inherited";

    private map: LocationMap | null = null;
    private addressInput: HTMLInputElement | null = null;
    private cityInput: HTMLInputElement | null = null;
    private zipCodeInput: HTMLInputElement | null = null;
    private stateInput: HTMLInputElement | null = null;
    private countryElement: HTMLSelectElement | null = null;
    private restrictionRadiusElement: HTMLSelectElement | null = null;
    private currentReadonly = true;
    private currentLatLng: { lat: number; lng: number } | null = null;
    private currentRestrictionRadius: number | null = null;
    private parentRestrictionRadius: number | null = null;
    private positionSectionVisible = true;
    private countryOptionsData: Country[] = [];
    private radiusOptionsData: LocationRestrictionRadiusOption[] = [];
    private initPromise: Promise<void> | null = null;

    public connectedCallback(): void {
        this.init().catch(() => { /* init failure is recoverable — next call retries */ });
    }

    public setInheritedRadiusLabel(label: string): void {
        const inheritedOption = this.getInheritedRadiusOption();
        if (inheritedOption) {
            inheritedOption.text = label;
        }
    }

    public setRestrictionRadiusSelection(radius: number | null | undefined, inherited: boolean | null | undefined): void {
        if (!this.restrictionRadiusElement) {
            return;
        }

        if (inherited && this.getInheritedRadiusOption()) {
            this.restrictionRadiusElement.value = LocationPanel.INHERITED_RADIUS_OPTION_VALUE;
            return;
        }

        this.restrictionRadiusElement.value = radius != null ? String(radius) : "";
    }

    public init(): Promise<void> {
        if (this.initPromise == null) {
            this.initPromise = this.doInit().catch(error => {
                this.initPromise = null;
                throw error;
            });
        }
        return this.initPromise;
    }

    private async doInit(): Promise<void> {
        const radiusInfoSlot = this.querySelector<HTMLElement>("[data-slot='restriction-info']");
        await loadGoogleMaps();
        const mapId = document.querySelector<HTMLMetaElement>("meta[name='google-maps-id']")?.content?.trim();

        const countryOptionsJSON = this.getAttribute("data-country-options");
        if (countryOptionsJSON) {
            this.countryOptionsData = JSON.parse(countryOptionsJSON) as Country[];
        }

        const radiusOptionsJSON = this.getAttribute("data-radius-options");
        if (radiusOptionsJSON) {
            this.radiusOptionsData = JSON.parse(radiusOptionsJSON) as LocationRestrictionRadiusOption[];
        }

        this.positionSectionVisible = this.getAttribute("hide-position-panel") !== "true";

        const countryOptionsHtml = [
            `<option value="" selected disabled hidden>${messages["locationPanel.country.placeholder"]}</option>`,
            ...this.countryOptionsData.map(c =>
                `<option value="${c.id}" data-code="${c.code}">${htmlEscape(c.name)}</option>`
            )
        ].join("");

        const radiusOptionsHtml = this.radiusOptionsData.map(o =>
            `<option value="${o.value}"${o.id ? ` id="${o.id}"` : ""}>${htmlEscape(o.label)}</option>`
        ).join("");
        const hasInheritedOption = this.radiusOptionsData.some(o => o.value === LocationPanel.INHERITED_RADIUS_OPTION_VALUE);
        const radiusSelectId = hasInheritedOption ? ` id="locationRestrictionRadiusSelect"` : "";

        this.innerHTML = `
            <div class="row" style="display:flex;align-items:stretch;">
                <div class="col-md-6" style="display:flex;flex-direction:column;">
                    <div class="location-panel-map" style="flex:1;min-height:300px;"></div>
                </div>
                <div class="col-md-6" style="display:flex;flex-direction:column;">
                    <div class="panel form-horizontal location-panel-address-panel">
                        <div class="panel-heading no-padding-bottom">
                            <h6 class="panel-title">${messages["locationPanel.address.header"]}</h6>
                        </div>
                        <div class="panel-body">
                            <div class="form-group">
                                <label class="control-label col-md-4">${messages["locationPanel.address"]}</label>
                                <div class="col-md-8">
                                    <input type="text" class="location-panel-address form-control" />
                                </div>
                            </div>
                            <div class="form-group">
                                <label class="control-label col-md-4">${messages["locationPanel.city"]}</label>
                                <div class="col-md-8">
                                    <input type="text" class="location-panel-city form-control" />
                                </div>
                            </div>
                            <div class="form-group">
                                <label class="control-label col-md-4">${messages["locationPanel.zipCode"]}</label>
                                <div class="col-md-8">
                                    <input type="text" class="location-panel-zip-code form-control" />
                                </div>
                            </div>
                            <div class="form-group">
                                <label class="control-label col-md-4">${messages["locationPanel.country"]}</label>
                                <div class="col-md-8">
                                    <select class="location-panel-country form-control">${countryOptionsHtml}</select>
                                </div>
                            </div>
                            <div class="form-group no-margin-bottom">
                                <label class="control-label col-md-4">${messages["locationPanel.state"]}</label>
                                <div class="col-md-8">
                                    <input type="text" class="location-panel-state form-control" />
                                </div>
                            </div>

                            <div class="alert alert-warning alert-styled-left no-margin-bottom mt-20 location-panel-reverse-geocode-warning" style="display:none">
                                <div>
                                    <div>${messages["locationPanel.geocode.warning.reverse"]}</div>
                                    <div class="mt-10">
                                        <button type="button" class="btn btn-primary location-panel-reverse-geocode-btn">
                                            <i class="far fa-map-marker-alt position-left"></i>${messages["locationPanel.geocode.reverse"]}
                                        </button>
                                        <button type="button" class="btn btn-default ml-5 location-panel-reverse-geocode-cancel-btn">
                                            ${messages["locationPanel.geocode.reverse.cancel"]}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="panel form-horizontal location-panel-position-section">
                        <div class="panel-heading no-padding-bottom">
                            <h6 class="panel-title">${messages["locationPanel.position"]}</h6>
                        </div>
                        <div class="panel-body">
                            <div class="form-group location-panel-no-position-info" style="display:none">
                                <div class="form-control-static ml-20">
                                    <i class="fal fa-info-circle position-left"></i> ${messages["locationPanel.restrictionRadius.noPosition.info"]}
                                </div>
                            </div>

                            <div class="form-group location-panel-position-display-row" style="display:none">
                                <label class="control-label col-md-4">${messages["locationPanel.locationLabel"]}</label>
                                <div class="col-md-8">
                                    <div class="form-control-static location-panel-position-display"></div>
                                </div>
                            </div>

                            <div class="form-group location-panel-restriction-row">
                                <label class="control-label col-md-4">${messages["locationPanel.restrictionRadius.label"]}</label>
                                <div class="col-md-5 location-panel-restriction-col">
                                    <select class="location-panel-restriction-radius form-control"${radiusSelectId}>${radiusOptionsHtml}</select>
                                    <div class="location-panel-restriction-info-slot"></div>
                                </div>
                                <div class="col-md-3 form-control-static location-panel-restriction-around">${messages["locationPanel.restrictionRadius.around"]}</div>
                            </div>

                            <div class="alert alert-warning alert-styled-left no-margin-bottom mt-20 location-panel-geocode-warning" style="display:none">
                                <div>
                                    <div>${messages["locationPanel.geocode.warning"]}</div>
                                    <div class="mt-10">
                                        <button type="button" class="btn btn-primary location-panel-geocode-btn">
                                            <i class="far fa-map-marker-alt position-left"></i>${messages["locationPanel.geocode"]}
                                        </button>
                                        <button type="button" class="btn btn-default ml-5 location-panel-geocode-cancel-btn">
                                            ${messages["locationPanel.geocode.cancel"]}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <button type="button" class="btn btn-outline-danger mt-20 mb-20 location-panel-delete-btn" style="align-self:flex-start">${messages["locationPanel.deleteLocation"]}</button>
                </div>
            </div>
        `;

        this.addressInput = this.querySelector<HTMLInputElement>(".location-panel-address");
        this.cityInput = this.querySelector<HTMLInputElement>(".location-panel-city");
        this.zipCodeInput = this.querySelector<HTMLInputElement>(".location-panel-zip-code");
        this.stateInput = this.querySelector<HTMLInputElement>(".location-panel-state");
        this.countryElement = this.querySelector<HTMLSelectElement>(".location-panel-country");
        this.restrictionRadiusElement = this.querySelector<HTMLSelectElement>(".location-panel-restriction-radius");

        const restrictionInfoContainer = this.querySelector<HTMLElement>(".location-panel-restriction-info-slot");
        if (restrictionInfoContainer && radiusInfoSlot) {
            restrictionInfoContainer.appendChild(radiusInfoSlot);
        }

        [this.addressInput, this.cityInput, this.zipCodeInput, this.stateInput, this.countryElement]
            .forEach(el => el?.addEventListener("change", () => this.setAddressChanged(true)));

        this.querySelector(".location-panel-geocode-btn")
            ?.addEventListener("click", () => void this.geocodeAddress());
        this.querySelector(".location-panel-geocode-cancel-btn")
            ?.addEventListener("click", () => this.setAddressChanged(false));

        this.querySelector(".location-panel-reverse-geocode-btn")
            ?.addEventListener("click", () => void this.reverseGeocode());
        this.querySelector(".location-panel-reverse-geocode-cancel-btn")
            ?.addEventListener("click", () => this.toggleReverseGeocodeWarning(false));

        this.querySelector(".location-panel-delete-btn")
            ?.addEventListener("click", () => this.clearAddressAndPosition());

        this.restrictionRadiusElement
            ?.addEventListener("change", () => this.syncRestrictionRadiusFromSelect());

        const mapElement = this.querySelector<HTMLElement>(".location-panel-map");
        if (!mapElement) {
            return;
        }

        this.map = new LocationMap({
            mapElement,
            mapId,
            ignoreRadius: this.radiusOptionsData.length <= 1,
            onPositionChanged: (latLng: google.maps.LatLngLiteral) => {
                this.currentLatLng = latLng;
                this.toggleReverseGeocodeWarning(true);
                this.updatePositionDisplay();
                this.dispatchPositionChanged();
            },
            onAutoComplete: (result) => {
                this.setData({
                    latitude: result.latitude,
                    longitude: result.longitude,
                    address: result.address,
                    city: result.city,
                    zipCode: result.zipCode,
                    state: result.state,
                    countryCode: result.countryCode,
                    locationRestrictionRadius: this.currentRestrictionRadius ?? undefined
                });
                this.toggleReverseGeocodeWarning(false);
                this.setAddressChanged(false);
                this.dispatchPositionChanged();
            }
        });

        this.setReadonly(this.currentReadonly);
        this.updatePositionPanelVisibility();
        this.updatePositionDisplay();
    }

    public setReadonly(readonly: boolean): void {
        this.currentReadonly = readonly;
        if (!this.map) {
            return;
        }

        this.map.toggleLocationControllable(!readonly);

        const inputs = [this.addressInput, this.cityInput, this.zipCodeInput, this.stateInput, this.countryElement];
        for (const input of inputs) {
            if (input) {
                input.disabled = readonly;
            }
        }

        if (readonly) {
            this.setAddressChanged(false);
            this.toggleReverseGeocodeWarning(false);
        }

        this.updatePositionDisplay();
    }

    public setPositionSectionVisible(visible: boolean): void {
        this.positionSectionVisible = visible;
        this.updatePositionPanelVisibility();
    }

    public setData(data: LocationPanelData): void {
        if (this.addressInput) {
            this.addressInput.value = data.address ?? "";
        }
        if (this.cityInput) {
            this.cityInput.value = data.city ?? "";
        }
        if (this.zipCodeInput) {
            this.zipCodeInput.value = data.zipCode ?? "";
        }
        if (this.stateInput) {
            this.stateInput.value = data.state ?? "";
        }

        if (this.countryElement) {
            this.applyCountrySelectValue(data);
        }

        if (data.latitude != null && data.longitude != null) {
            this.currentLatLng = {lat: data.latitude, lng: data.longitude};
        } else {
            this.currentLatLng = null;
        }

        if (data.locationRestrictionRadius !== undefined) {
            this.currentRestrictionRadius = data.locationRestrictionRadius ?? null;
        }

        this.map?.setPosition(this.currentLatLng);
        this.map?.setRestrictionRadius(this.currentRestrictionRadius);

        if (this.restrictionRadiusElement && this.restrictionRadiusElement.value !== LocationPanel.INHERITED_RADIUS_OPTION_VALUE) {
            this.restrictionRadiusElement.value = this.currentRestrictionRadius != null
                ? String(this.currentRestrictionRadius)
                : "";
        }

        this.updatePositionDisplay();
    }

    public getData(): LocationPanelData {
        const countrySelect = this.countryElement;
        const countryId = countrySelect?.value ? Number(countrySelect.value) : undefined;
        const countryName = countrySelect?.options[countrySelect.selectedIndex]?.text?.trim() || undefined;

        return {
            latitude: this.currentLatLng?.lat,
            longitude: this.currentLatLng?.lng,
            address: this.addressInput?.value || undefined,
            city: this.cityInput?.value || undefined,
            zipCode: this.zipCodeInput?.value || undefined,
            state: this.stateInput?.value || undefined,
            countryId,
            countryName,
            locationRestrictionRadius: this.currentRestrictionRadius ?? undefined,
            locationRestrictionInherited: this.restrictionRadiusElement?.value === LocationPanel.INHERITED_RADIUS_OPTION_VALUE
        };
    }

    public refreshMap(): void {
        this.map?.refreshView();
    }

    public reset(): void {
        this.setAddressChanged(false);
        this.toggleReverseGeocodeWarning(false);
        this.currentRestrictionRadius = null;
        this.setData({});
        this.map?.resetToDefault();
        this.updatePositionDisplay();
    }

    public setRestrictionRadius(radius: number | null | undefined): void {
        this.currentRestrictionRadius = radius ?? null;
        this.map?.setRestrictionRadius(this.currentRestrictionRadius);
        if (this.restrictionRadiusElement && this.restrictionRadiusElement.value !== LocationPanel.INHERITED_RADIUS_OPTION_VALUE) {
            this.restrictionRadiusElement.value = this.currentRestrictionRadius != null
                ? String(this.currentRestrictionRadius)
                : "";
        }
    }

    public setParentRestrictionRadius(radius: number | null | undefined): void {
        this.parentRestrictionRadius = radius ?? null;
    }

    private applyCountrySelectValue(data: LocationPanelData): void {
        const countrySelect = this.countryElement;

        if (data.countryId != null) {
            countrySelect.value = String(data.countryId);
            return;
        }

        if (data.countryCode) {
            const option = countrySelect.querySelector<HTMLOptionElement>(`option[data-code="${data.countryCode}"]`);
            if (option) {
                countrySelect.value = option.value;
                return;
            }
        }

        if (data.countryName) {
            const byName = Array.from(countrySelect.options)
                .find(option => option.text.trim().toLowerCase() === data.countryName?.trim().toLowerCase());
            if (byName) {
                countrySelect.value = byName.value;
                return;
            }
        }

        countrySelect.value = "";
    }

    private syncRestrictionRadiusFromSelect(): void {
        if (!this.restrictionRadiusElement) {
            return;
        }

        const selected = this.restrictionRadiusElement.value;
        const inherited = selected === LocationPanel.INHERITED_RADIUS_OPTION_VALUE;

        if (inherited) {
            this.currentRestrictionRadius = this.parentRestrictionRadius;
        } else {
            const numericValue = Number(selected);
            this.currentRestrictionRadius = Number.isFinite(numericValue) && selected !== "" ? numericValue : null;
        }

        this.map?.setRestrictionRadius(this.currentRestrictionRadius);
    }

    private clearAddressAndPosition(): void {
        this.currentRestrictionRadius = null;
        if (this.restrictionRadiusElement) {
            this.restrictionRadiusElement.value = "";
        }

        this.setAddressChanged(false);
        this.toggleReverseGeocodeWarning(false);
        this.setData({});
        this.dispatchPositionChanged();
    }

    private dispatchPositionChanged(): void {
        this.dispatchEvent(new CustomEvent(LocationPanel.EVENT_POSITION_CHANGED, {
            detail: this.currentLatLng,
            bubbles: true
        }));
    }

    private setAddressChanged(changed: boolean): void {
        const warning = this.querySelector<HTMLElement>(".location-panel-geocode-warning");
        if (warning) {
            warning.style.display = changed && !this.currentReadonly && this.positionSectionVisible ? "" : "none";
        }

        if (changed) {
            this.dispatchEvent(new CustomEvent(LocationPanel.EVENT_ADDRESS_CHANGED, {bubbles: true}));
        }
    }

    private toggleReverseGeocodeWarning(show: boolean): void {
        const warning = this.querySelector<HTMLElement>(".location-panel-reverse-geocode-warning");
        if (warning) {
            warning.style.display = show && !this.currentReadonly ? "" : "none";
        }
    }

    private updatePositionPanelVisibility(): void {
        const section = this.querySelector<HTMLElement>(".location-panel-position-section");
        if (section) {
            section.style.display = this.positionSectionVisible ? "" : "none";
            if (this.positionSectionVisible) {
                section.style.marginTop = "20px";
                section.style.marginBottom = "0";
            }
        }

        const addressPanel = this.querySelector<HTMLElement>(".location-panel-address-panel");
        if (addressPanel) {
            addressPanel.style.marginBottom = "0";
        }
    }

    private updatePositionDisplay(): void {
        const display = this.querySelector<HTMLElement>(".location-panel-position-display");
        const displayRow = this.querySelector<HTMLElement>(".location-panel-position-display-row");
        const noPositionInfo = this.querySelector<HTMLElement>(".location-panel-no-position-info");
        const restrictionRow = this.querySelector<HTMLElement>(".location-panel-restriction-row");
        const deleteButton = this.querySelector<HTMLButtonElement>(".location-panel-delete-btn");

        const latLng = this.currentLatLng;
        const hasPosition = latLng != null;

        if (display) {
            display.textContent = hasPosition
                ? `${latLng.lat.toFixed(4)}, ${latLng.lng.toFixed(4)}`
                : "";
        }

        if (displayRow) {
            displayRow.style.display = hasPosition ? "" : "none";
        }

        if (noPositionInfo) {
            noPositionInfo.style.display = hasPosition ? "none" : "";
        }

        if (restrictionRow) {
            restrictionRow.style.marginBottom = hasPosition ? "0" : "";
        }

        if (this.restrictionRadiusElement) {
            const radiusDisabled = this.radiusOptionsData.length <= 1;
            this.restrictionRadiusElement.disabled = radiusDisabled || this.currentReadonly || !hasPosition;

            const restrictionAround = this.querySelector<HTMLElement>(".location-panel-restriction-around");
            if (restrictionAround) {
                restrictionAround.style.display = radiusDisabled ? "none" : "";
            }

            const restrictionCol = this.querySelector<HTMLElement>(".location-panel-restriction-col");
            if (restrictionCol) {
                restrictionCol.classList.toggle("col-md-5", !radiusDisabled);
                restrictionCol.classList.toggle("col-md-8", radiusDisabled);
            }
        }

        if (deleteButton) {
            deleteButton.style.display = this.currentReadonly ? "none" : "";
        }
    }

    private getInheritedRadiusOption(): HTMLOptionElement | null {
        if (!this.restrictionRadiusElement) {
            return null;
        }

        return this.restrictionRadiusElement.querySelector<HTMLOptionElement>(
            `option[value="${LocationPanel.INHERITED_RADIUS_OPTION_VALUE}"]`
        );
    }

    private async geocodeAddress(): Promise<void> {
        const data = this.getData();
        if (!data.countryName && this.countryElement) {
            const countrySelect = this.countryElement;
            data.countryName = countrySelect.options[countrySelect.selectedIndex]?.text?.trim() || undefined;
        }

        try {
            const latLng = await GeocodeService.lookupAddress(data);
            this.map?.setPosition(latLng);
            this.currentLatLng = latLng;
            this.updatePositionDisplay();
            this.dispatchPositionChanged();
            this.setAddressChanged(false);
        } catch {
            // geocode failed — leave the warning visible so user knows
        }
    }

    private async reverseGeocode(): Promise<void> {
        if (!this.currentLatLng) {
            return;
        }

        try {
            const result = await GeocodeService.lookupLatLng(this.currentLatLng.lat, this.currentLatLng.lng);
            this.setData({
                latitude: this.currentLatLng.lat,
                longitude: this.currentLatLng.lng,
                address: result.address,
                city: result.city,
                zipCode: result.zipCode,
                state: result.state,
                countryCode: result.countryCode,
                locationRestrictionRadius: this.currentRestrictionRadius ?? undefined
            });
            this.toggleReverseGeocodeWarning(false);
        } catch {
            // reverse geocode failed — leave warning visible
        }
    }
}
